import type { RouteLocationNormalized } from 'vue-router'
import { useAuthStore } from '@/core/auth/auth'
import { getDefaultAuthorizedRoute } from '@/core/auth/landing'
import { can } from '@/core/permissions/can'
import { pagePermissionForRoute } from '@/core/permissions/registry'

const AUTH_REDIRECT = '/auth/signin'
const FORBIDDEN_REDIRECT = '/403'

const isInvitationSignupRoute = (to: RouteLocationNormalized): boolean => {
  return to.path === '/auth/signup' && typeof to.query.token === 'string' && to.query.token.length > 0
}

const isGuestOnlyRoute = (to: RouteLocationNormalized): boolean => {
  return to.path === '/auth/signin' || isInvitationSignupRoute(to)
}

export const resolveAuthNavigation = (to: RouteLocationNormalized): true | string => {
  const authStore = useAuthStore()

  // Open /auth/signup without invite token is not allowed
  if (to.path === '/auth/signup' && !isInvitationSignupRoute(to)) {
    return AUTH_REDIRECT
  }

  // Invitation signup must bypass auth redirects.
  if (isInvitationSignupRoute(to)) {
    return true
  }

  const isAuthenticated = authStore.checkAuth()
  const requiresAuth = to.meta.requiresAuth === true

  if (requiresAuth && !isAuthenticated) {
    return AUTH_REDIRECT
  }

  if (isAuthenticated && isGuestOnlyRoute(to)) {
    return getDefaultAuthorizedRoute(authStore.user)
  }

  if (isAuthenticated) {
    // Redirect authenticated users without page.dashboard permission to their default authorized route when landing on '/'
    if (to.path === '/' && !can('page.dashboard')) {
      return getDefaultAuthorizedRoute(authStore.user)
    }

    const userRole = authStore.user?.role

    const requiredKeys: string[] = []
    if (typeof to.meta.permission === 'string') {
      requiredKeys.push(to.meta.permission)
    }
    if (Array.isArray(to.meta.permissions)) {
      for (const key of to.meta.permissions) {
        if (typeof key === 'string' && key && !requiredKeys.includes(key)) {
          requiredKeys.push(key)
        }
      }
    }

    // 1. Route has permission key(s) — allow if the user has ANY of them
    if (requiredKeys.length > 0) {
      if (!requiredKeys.some((key) => can(key))) {
        return FORBIDDEN_REDIRECT
      }
    }
    // 2. Derive permission from registry matching if available
    else {
      const derived = pagePermissionForRoute(to)
      if (derived) {
        if (!can(derived)) {
          return FORBIDDEN_REDIRECT
        }
      }
      // 3. Fallback to static role-based access check
      else if (Array.isArray(to.meta.allowedRoles) && to.meta.allowedRoles.length > 0) {
        if (!userRole || !to.meta.allowedRoles.includes(userRole)) {
          return FORBIDDEN_REDIRECT
        }
      }
    }
  }

  return true
}

